import Button from 'components/actions/Button';
import type { Position } from 'components/map/types';
import { useState } from 'react';

import type { HereRoute } from './hereRouting';
import { haversineMeters } from './interpolation';

type RouteInfoPanelProps = {
    positions: Position[];
    route: HereRoute | undefined;
    onCalculate: () => Promise<void>;
};

const formatDistance = (meters: number): string => {
    if (meters < 1000) return `${Math.round(meters)} m`;
    return `${(meters / 1000).toFixed(1)} km`;
};

const formatDuration = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const min = Math.floor((seconds % 3600) / 60);
    if (h === 0) return `${min} min`;
    if (min === 0) return `${h} h`;
    return `${h} h ${min} min`;
};

const Stat = ({ label, value }: { label: string; value: string | number }) => (
    <div className='RouteBuilder__stat'>
        <div className='RouteBuilder__stat-label'>{label}</div>
        <div className='RouteBuilder__stat-value'>{value}</div>
    </div>
);

const RouteInfoPanel = ({ positions, route, onCalculate }: RouteInfoPanelProps) => {
    const [isCalculating, setIsCalculating] = useState(false);
    const [error, setError] = useState<string>();

    const start = positions.at(0);
    const destination = positions.at(-1);

    const handleCalculate = async () => {
        setIsCalculating(true);
        setError(undefined);
        try {
            await onCalculate();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Route calculation failed');
        } finally {
            setIsCalculating(false);
        }
    };

    return (
        <div className='display-flex flex-column gap-10'>
            {error && <p className='text-color-danger'>{error}</p>}

            {route && (
                <div className='RouteBuilder__route-stats'>
                    <Stat label='Distance' value={formatDistance(route.distanceMeters)} />
                    <Stat label='Duration' value={formatDuration(route.durationSeconds)} />
                    <Stat label='Points' value={route.positions.length} />
                    {start && destination && (
                        <Stat label='Straight-line' value={formatDistance(haversineMeters(start, destination))} />
                    )}
                </div>
            )}

            <Button
                style='primary'
                size='sm'
                className='width-100pct'
                disabled={positions.length < 2}
                isLoading={isCalculating}
                onClick={handleCalculate}
            >
                {!isCalculating && <span className='rioglyph rioglyph-route margin-right-5' />}
                {isCalculating ? 'Calculating…' : 'Calculate route'}
            </Button>
        </div>
    );
};

export default RouteInfoPanel;
